import { Box, Stack, Typography } from '@mui/material';
import color from '@theme/Colors';
import { ReactNode } from 'react';

export type SummaryStatTone = 'success' | 'error' | 'warning' | 'default';

export type SummaryStatItem = {
    label: string;
    value: ReactNode;
    tone?: SummaryStatTone;
};

export type SummaryStatVariant = 'connected' | 'separated';

type Props = {
    items: SummaryStatItem[];
    columns?: number;
    variant?: SummaryStatVariant;
};

const toneToColor: Record<SummaryStatTone, string> = {
    success: color.success,
    error: color.danger,
    warning: color.gold700,
    default: color.textPrimary,
};

export default function SummaryStatCard(props: Props) {
    const { items, columns = 3, variant = 'connected' } = props;
    const isSeparated = variant === 'separated';

    if (isSeparated) {
        return (
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: `repeat(${columns}, 1fr)`,
                    gap: 1,
                }}
            >
                {items.map((item, idx) => (
                    <Stack
                        key={`${item.label}-${idx}`}
                        sx={{
                            px: 1.5,
                            py: 1,
                            border: `1px solid ${color.grey200}`,
                            borderRadius: 1,
                            bgcolor: color.white,
                        }}
                    >
                        <Typography variant="body2" sx={{ color: color.grey600 }}>
                            {item.label}
                        </Typography>
                        <Typography
                            variant="body1"
                            fontWeight={600}
                            sx={{ color: toneToColor[item.tone ?? 'default'] }}
                        >
                            {item.value}
                        </Typography>
                    </Stack>
                ))}
            </Box>
        );
    }

    return (
        <Box
            sx={{
                border: `1px solid ${color.grey200}`,
                borderRadius: 1,
                bgcolor: color.grey100,
                overflow: 'hidden',
                display: 'grid',
                gridTemplateColumns: `repeat(${columns}, 1fr)`,
            }}
        >
            {items.map((item, idx) => {
                const row = Math.floor(idx / columns);
                const col = idx % columns;
                const isLastCol = col === columns - 1;
                const isLastRow = row === Math.floor((items.length - 1) / columns);

                return (
                    <Stack
                        key={`${item.label}-${idx}`}
                        sx={{
                            px: 1.5,
                            py: 1,
                            borderRight: isLastCol ? 'none' : `1px solid ${color.grey200}`,
                            borderBottom: isLastRow ? 'none' : `1px solid ${color.grey200}`,
                        }}
                    >
                        <Typography variant="body2" sx={{ color: color.grey600 }}>
                            {item.label}
                        </Typography>
                        <Typography
                            variant="body2"
                            fontWeight={600}
                            sx={{ color: toneToColor[item.tone ?? 'default'] }}
                        >
                            {item.value}
                        </Typography>
                    </Stack>
                );
            })}
        </Box>
    );
}
